import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useRunSegments } from "@/hooks/useRunSegments";
import { useUserVehicles } from "@/hooks/useUserVehicles";
import RunPhotoManager from "@/components/RunPhotoManager";
import RunWaypointEditor from "@/components/RunWaypointEditor";
import RunPrivacyControl from "@/components/RunPrivacyControl";
import TrackEditor from "@/components/TrackEditor";
import LockedContent from "@/components/LockedContent";
import { type RunWaypoint } from "@/hooks/useRunWaypoints";

const runEditSchema = z.object({
  description: z.string().max(1000, "Description must be less than 1000 characters").optional(),
  equipment_type: z.enum(['suv', 'truck', 'sxs', 'atv', 'dirt_bike', 'other']),
  vehicle_id: z.string().nullable().optional(),
  visibility: z.enum(['public', 'private', 'unlisted', 'anonymous']).default('public'),
});

type RunEditFormData = z.infer<typeof runEditSchema>;

interface RunData {
  id: string;
  user_id: string;
  description: string | null;
  equipment_type: string;
  photo_urls: any;
  vehicle_id: string | null;
  visibility: 'public' | 'private' | 'unlisted' | 'anonymous';
}

const RunEdit = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [runData, setRunData] = useState<RunData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trackGeometry, setTrackGeometry] = useState<any>(null);
  const { segments: runSegments } = useRunSegments(runId);
  const { vehicles } = useUserVehicles(user?.id || '');

  const form = useForm<RunEditFormData>({
    resolver: zodResolver(runEditSchema),
    defaultValues: {
      description: '',
      equipment_type: 'suv',
      vehicle_id: null,
      visibility: 'public',
    },
  });

  useEffect(() => {
    if (runId) {
      fetchRunData();
      fetchRunTracks();
    }
  }, [runId]);

  const fetchRunData = async () => {
    try {
      const { data, error } = await supabase
        .from('runs')
        .select('*')
        .eq('id', runId)
        .single();

      if (error) throw error;

      if (data.user_id !== user?.id) {
        toast({
          title: "Access denied",
          description: "You don't have permission to edit this run.",
          variant: "destructive",
        });
        navigate(`/runs/${runId}`);
        return;
      }

      setRunData(data);
      form.reset({
        description: data.description || '',
        equipment_type: data.equipment_type as any,
        vehicle_id: data.vehicle_id,
        visibility: data.visibility || 'public',
      });
    } catch (error) {
      console.error('Error fetching run data:', error);
      toast({
        title: "Error loading run",
        description: "Failed to load run details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRunTracks = async () => {
    try {
      const { data, error } = await supabase
        .from('run_tracks')
        .select('geom, seq')
        .eq('run_id', runId)
        .order('seq');

      if (error) throw error;

      if (data && data.length > 0) {
        const coordinates = data.map(track => {
          const geom = track.geom as any;
          return [geom.coordinates[0], geom.coordinates[1]];
        });

        setTrackGeometry({
          type: 'LineString',
          coordinates
        });
      }
    } catch (error) {
      console.error('Error fetching run tracks:', error);
    }
  };

  const onSubmit = async (data: RunEditFormData) => {
    if (!runId) return;

    setSaving(true);
    try {
      // If a vehicle is selected, derive equipment_type from the vehicle
      let equipment_type = data.equipment_type;
      if (data.vehicle_id && vehicles) {
        const selectedVehicle = vehicles.find(v => v.id === data.vehicle_id);
        if (selectedVehicle?.vehicle_type) {
          equipment_type = selectedVehicle.vehicle_type;
        }
      }

      const { error } = await supabase
        .from('runs')
        .update({
          description: data.description,
          equipment_type,
          vehicle_id: data.vehicle_id || null,
          visibility: data.visibility,
        })
        .eq('id', runId);

      if (error) throw error;

      toast({
        title: "Run updated",
        description: "Your changes have been saved successfully.",
      });

      navigate(`/runs/${runId}`);
    } catch (error) {
      console.error('Error updating run:', error);
      toast({
        title: "Error saving changes",
        description: "Failed to update run. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePhotosUpdate = (photos: string[]) => {
    if (runData) {
      setRunData({ ...runData, photo_urls: photos });
    }
  };

  const handleWaypointsComplete = async (waypoints: Partial<RunWaypoint>[]) => {
    toast({
      title: "Waypoints saved",
      description: "Your waypoint changes have been saved.",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading run details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!runData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground mb-4">Run Not Found</h1>
            <p className="text-muted-foreground">The requested run could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/runs/${runId}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Run
            </Button>
          </div>

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Run Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form className="space-y-4">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Run Story / Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Share the story of your adventure..."
                            className="min-h-32"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicle_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vehicle Used</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a vehicle..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">Not specified</SelectItem>
                            {vehicles?.map((vehicle) => (
                              <SelectItem key={vehicle.id} value={vehicle.id}>
                                {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Show equipment type dropdown only if no vehicle selected */}
                  {(!form.watch('vehicle_id') || form.watch('vehicle_id') === 'none') && (
                    <FormField
                      control={form.control}
                      name="equipment_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Equipment Type</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="suv">SUV / Jeep</SelectItem>
                              <SelectItem value="truck">Truck</SelectItem>
                              <SelectItem value="sxs">Side-by-Side (SxS)</SelectItem>
                              <SelectItem value="atv">ATV</SelectItem>
                              <SelectItem value="dirt_bike">Dirt Bike</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <Button
                    type="submit"
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Details'
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Privacy Control */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy Settings</CardTitle>
              <CardDescription>
                Control who can see this run
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RunPrivacyControl
                visibility={form.watch('visibility')}
                onChange={(visibility) => form.setValue('visibility', visibility)}
                disabled={saving}
              />
            </CardContent>
          </Card>

          {/* Photo Management */}
          <RunPhotoManager
            photos={Array.isArray(runData.photo_urls) ? runData.photo_urls : []}
            runId={runId!}
            segments={runSegments}
            onPhotosUpdate={handlePhotosUpdate}
          />

          {/* Waypoint Editor */}
          <RunWaypointEditor
            runId={runId!}
            trackGeometry={trackGeometry}
            photos={Array.isArray(runData.photo_urls) ? runData.photo_urls : []}
            onComplete={handleWaypointsComplete}
            onBack={() => {}}
          />

          {/* Track Editor */}
          {trackGeometry && (
            <LockedContent
              requiredTier="adventurer"
              featureDescription="edit and clean GPS tracks"
              blurContent={true}
            >
              <TrackEditor
                runId={runId!}
                onSave={() => {
                  fetchRunTracks();
                  toast({
                    title: "Track updated",
                    description: "Your track has been cleaned and saved.",
                  });
                }}
              />
            </LockedContent>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunEdit;
